import c from 'classnames';
import React from 'react';

import {
  GutBodyConnectionsCondition,
  GutBodyConnectionsOrgan,
} from '@repo/types';
import {
  Button,
  ButtonColors,
  GutBodyConnectionsBody,
  Typography,
} from '@repo/ui';
import useGutBodyProps from 'hooks/useGutBodyProps';
import { SLIDE_BACKGROUND_COLORS } from '../slideBackgroundColors';
import styles from './ConditionSpotlight.module.css';
import {
  conditionFlagsLabel,
  conditionSpotlightSlideId,
} from './conditionsSlides';
import slideStyles from './Slides.module.css';
import { useConditionsSlide } from './useConditionsSlide';

const FLAGS_INTRO_COPY =
  'Explore flagged metrics to learn more about how you can improve them.';
const NO_FLAGS_COPY =
  'Address top imbalances and explore other factors such as your environment, diet etc.';

interface ConditionSpotlightProps {
  organ: GutBodyConnectionsOrgan;
  condition: GutBodyConnectionsCondition;
}

export const ConditionSpotlight: React.FC<ConditionSpotlightProps> = ({
  organ,
  condition,
}) => {
  const { age, pregnant, sex } = useGutBodyProps();
  const { isFirstView, viewConditions } = useConditionsSlide({
    slideId: conditionSpotlightSlideId(condition),
    condition: condition.condition,
    viewed: {
      condition_name: condition.display_name,
      body_area: organ.lookup_code,
      flags_found: condition.flagged_count,
      flags_total: condition.total_count,
    },
    ctaClicked: { condition_name: condition.display_name },
  });

  const hasFlags = condition.flagged_count > 0;

  return (
    <div
      className={c(slideStyles.slideWrapper, styles.slide)}
      style={{ backgroundColor: SLIDE_BACKGROUND_COLORS.conditionSpotlight }}
    >
      <div className={styles.header}>
        <Typography variant='body-s' color='grey'>
          Because you reported experiencing
        </Typography>
        <Typography
          variant='display-m'
          className={c(styles.title, styles.slideInRight)}
        >
          {condition.display_name}
        </Typography>
      </div>

      <div className={styles.bodyWrap}>
        <div className={styles.bodyCircleCrop}>
          <GutBodyConnectionsBody
            className={styles.bodyInsideCircle}
            organs={[organ]}
            pulseUnlit
            colorVariant='orange'
            age={age}
            pregnant={pregnant}
            sex={sex}
            showDotAndTooltips={false}
          />
        </div>
      </div>

      <div className={c(styles.found, styles.slideInRight)}>
        <Typography variant='eyebrow' color='grey'>
          Here&apos;s what we found:
        </Typography>
        <Typography variant='heading-xl' className={styles.foundHeading}>
          {conditionFlagsLabel(condition)}
        </Typography>
        <Typography variant='body-s' color='grey'>
          {hasFlags ? FLAGS_INTRO_COPY : NO_FLAGS_COPY}
        </Typography>
      </div>

      {!isFirstView && (
        <div className={c(styles.buttons)}>
          <Button
            variant='outline'
            color={ButtonColors.PURPLE}
            onClick={viewConditions}
          >
            View condition associations
          </Button>
        </div>
      )}
    </div>
  );
};
